import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "tailwindcss/tailwind.css";
import { BASE_URL } from "../../config";
import { motion } from "framer-motion";

const EditIcd = () => {
  const navigate = useNavigate();
  const { icdCode } = useParams();
  const [formData, setFormData] = useState({
    icd_10_cm: "",
    icd_9_cm: "",
    condition: "",
    categ_code: "",
    categ_desc: "",
    level_code: "",
    level_desc: "",
  });
  const [loadingIcd, setLoadingIcd] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchIcd = async () => {
      setLoadingIcd(true);
      try {
        const response = await axios.get(`${BASE_URL}/icd/${icdCode}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
          },
        });
        const { icd } = response.data.data;
        setFormData({
          icd_10_cm: icd.icd_10_cm,
          icd_9_cm: icd.icd_9_cm || "",
          condition: icd.condition || "",
          categ_code: icd.categ_code || "",
          categ_desc: icd.categ_desc || "",
          level_code: icd.level_code || "",
          level_desc: icd.level_desc || "",
        });
      } catch (error) {
        toast.error("Failed to fetch ICD data.", { autoClose: 3000 });
        setTimeout(() => navigate("/icd"), 2000);
      } finally {
        setLoadingIcd(false);
      }
    };

    fetchIcd();
  }, [icdCode, navigate]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.icd_10_cm) newErrors.icd_10_cm = "ICD-10-CM code is required.";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      Object.values(formErrors).forEach((error) => toast.error(error, { autoClose: 3000 }));
      return;
    }
    try {
      const response = await axios.put(
        `${BASE_URL}/icd/${icdCode}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
          },
        }
      );
      toast.success("ICD code updated successfully.", { autoClose: 3000 });
      setTimeout(() => navigate("/icd"), 2000);
    } catch (error) {
      if (error.response?.status === 422 && error.response?.data?.message) {
        if (typeof error.response.data.message === "string") {
          toast.error(error.response.data.message, { autoClose: 3000 });
        } else {
          Object.values(error.response.data.message).forEach((messages) => {
            const messageArray = Array.isArray(messages) ? messages : [messages];
            messageArray.forEach((message) => toast.error(message, { autoClose: 3000 }));
          });
        }
      } else {
        toast.error("Failed to update ICD code.", { autoClose: 3000 });
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-2 px-2 sm:px-6 lg:px-8">
      <ToastContainer position="top-right" autoClose={3000} />
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-3xl mx-auto"
      >
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Edit ICD Code: {icdCode}</h1>
        <div className="bg-white p-6 rounded-lg shadow-md">
          {loadingIcd ? (
            <div className="text-center text-sm text-gray-500">Loading...</div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">ICD-10-CM *</label>
                  <input
                    type="text"
                    name="icd_10_cm"
                    value={formData.icd_10_cm}
                    disabled
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 bg-gray-100 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">ICD-9-CM</label>
                  <input
                    type="text"
                    name="icd_9_cm"
                    value={formData.icd_9_cm}
                    onChange={handleFormChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Condition</label>
                  <input
                    type="text"
                    name="condition"
                    value={formData.condition}
                    onChange={handleFormChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Category Code</label>
                  <input
                    type="text"
                    name="categ_code"
                    value={formData.categ_code}
                    onChange={handleFormChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Category Description</label>
                  <input
                    type="text"
                    name="categ_desc"
                    value={formData.categ_desc}
                    onChange={handleFormChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Level Code</label>
                  <input
                    type="text"
                    name="level_code"
                    value={formData.level_code}
                    onChange={handleFormChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Level Description</label>
                  <input
                    type="text"
                    name="level_desc"
                    value={formData.level_desc}
                    onChange={handleFormChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  type="submit"
                  className="bg-green-600 text-white px-4 py-2 rounded-md text-sm hover:bg-green-700"
                >
                  Save
                </button>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default EditIcd;